import React, { useState, useEffect } from 'react';
import { competitions } from '../data';
import './Competitions.css';

/* ── Clean Inline SVG Icons (Executive Style - No Emojis) ──── */
const IconArrowUpRight = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="7" y1="17" x2="17" y2="7"></line>
    <polyline points="7 7 17 7 17 17"></polyline>
  </svg>
);

const IconDocument = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
  </svg>
);

const IconMaximize = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="15 3 21 3 21 9"></polyline>
    <polyline points="9 21 3 21 3 15"></polyline>
    <line x1="21" y1="3" x2="14" y2="10"></line>
    <line x1="3" y1="21" x2="10" y2="14"></line>
  </svg>
);

const IconStar = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="currentColor"
    strokeWidth="1"
    aria-hidden="true"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
  </svg>
);

const IconGlobe = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" y1="12" x2="22" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

const IconClose = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

/* ── Lightbox Modal Component ───────────────────────────── */
const LightboxModal = ({ image, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!image) return null;

  return (
    <div className="comp-lightbox-overlay" onClick={onClose}>
      <div className="comp-lightbox-header" onClick={(e) => e.stopPropagation()}>
        <div className="comp-lightbox-title">{image.title || 'Official Certificate & Record'}</div>
        <button
          type="button"
          className="comp-lightbox-close"
          onClick={onClose}
          aria-label="Close Lightbox"
        >
          <IconClose />
        </button>
      </div>

      <div className="comp-lightbox-body" onClick={(e) => e.stopPropagation()}>
        <img
          src={image.src || image.image}
          alt={image.title || 'Certificate'}
          className="comp-lightbox-img"
        />
      </div>

      <div className="comp-lightbox-footer" onClick={(e) => e.stopPropagation()}>
        <div className="comp-lightbox-caption">{image.caption || ''}</div>
        <a
          href={image.src || image.image}
          target="_blank"
          rel="noopener noreferrer"
          className="comp-lightbox-download"
        >
          <span>Open Full Resolution</span>
          <IconArrowUpRight />
        </a>
      </div>
    </div>
  );
};

/* ── Main Competitions Component ────────────────────────── */
const Competitions = () => {
  const [activeStage, setActiveStage] = useState('algothon');
  const [selectedYear, setSelectedYear] = useState('All');
  const [lightboxImg, setLightboxImg] = useState(null);

  // Gallery active indices
  const [algoImgIdx, setAlgoImgIdx] = useState(0);
  const [uojImgIdx, setUojImgIdx] = useState(0);
  const [kaprukaTab, setKaprukaTab] = useState('live');

  // Algothon Images
  const algothonGallery = [
    {
      id: 'algo-cert',
      title: 'Merit Award Certificate',
      src: '/images/algothon/certificate.jpg',
      caption: 'Official Merit Award Certificate — CODEFEST 2026 Algothon (Tertiary Category), SLIIT Faculty of Computing',
    },
    {
      id: 'algo-team',
      title: 'Team CodeMode',
      src: '/images/algothon/1790614201301.jpg',
      caption: 'Team CodeMode representing SLIIT Northern UNI at CODEFEST 2026 Algothon',
    },
    {
      id: 'algo-coding',
      title: 'Contest Floor',
      src: '/images/algothon/1790614201417.jpg',
      caption: 'Marisilin Bijosilin & CodeMode team tackling complex algorithmic problems under time pressure',
    },
    {
      id: 'algo-arena',
      title: 'Live Arena',
      src: '/images/algothon/1790614200710.jpg',
      caption: '8-Hour Algothon Live Arena & Countdown Screen at Faculty of Computing, SLIIT',
    },
    {
      id: 'algo-stage',
      title: 'Event Stage',
      src: '/images/algothon/1790614201065.jpg',
      caption: 'SLIIT CODEFEST 2026 — Algothon Official Stage & Branding',
    },
    {
      id: 'algo-launch',
      title: 'Inauguration',
      src: '/images/algothon/1790614201680.jpg',
      caption: 'Official Contest Launch by SLIIT Faculty of Computing Deans & Dignitaries',
    },
  ];

  // UOJ Coders Images
  const uojGallery = [
    {
      id: 'uoj-cert',
      title: 'Official Certificate',
      src: '/images/certifi-uoj.jpg',
      caption: 'UoJ Coders v5.0 Official Certificate of Participation — University of Jaffna',
    },
    {
      id: 'uoj-team',
      title: 'Team Photo',
      src: '/images/UOJ1.jpeg',
      caption: 'Team Git_Force_Push representing SLIIT Northern UNI at UoJ Coders v5.0',
    },
    {
      id: 'uoj-arena',
      title: 'Arena Battle',
      src: '/images/UOJ2.jpeg',
      caption: '12-Hour Late-Night Competitive Programming Crucible at University of Jaffna',
    },
    {
      id: 'uoj-award',
      title: 'Awarding Ceremony',
      src: '/images/UOJ3.jpeg',
      caption: 'UoJ Coders v5.0 Awarding & Recognition Ceremony',
    },
  ];

  const currentAlgoImg = algothonGallery[algoImgIdx];
  const currentUojImg = uojGallery[uojImgIdx];

  const filteredCompetitions = selectedYear === 'All'
    ? competitions
    : competitions.filter(c => c.year === selectedYear);

  return (
    <section id="competitions" className="sec comp-arena-sec">
      <div className="comp-ambient-glow" />

      {/* Section Header */}
      <div className="sec-inner" style={{ position: 'relative', zIndex: 1 }}>
        <div className="sec-label">10 — Competitions & Hackathons</div>
        <h2 className="sec-title">
          Battle-Tested<br />
          <em>Competitive Battles & Awards</em>
        </h2>
        <p className="sec-desc" style={{ marginTop: '0.5rem' }}>
          Over 3 years of algorithmic battles, overnight competitive crucibles, and national hackathons across Sri Lanka’s premier computing institutions.
        </p>
      </div>

      {/* ── Honor Metrics Ribbon (Using Official Logos) ──── */}
      <div className="comp-honor-strip reveal">
        <div className="comp-honor-card gold">
          <div className="comp-honor-top">
            <img
              src="/images/codefest2026.png"
              alt="SLIIT CODEFEST"
              className="comp-honor-logo"
            />
            <span className="comp-honor-count">1</span>
          </div>
          <div className="comp-honor-label">National Merit Award</div>
          <div className="comp-honor-sub">SLIIT CODEFEST 2026 Algothon (Tertiary)</div>
        </div>

        <div className="comp-honor-card red">
          <div className="comp-honor-top">
            <img
              src="/images/uoj_logo.png"
              alt="University of Jaffna"
              className="comp-honor-logo"
            />
            <span className="comp-honor-count">2×</span>
          </div>
          <div className="comp-honor-label">Islandwide Top 10</div>
          <div className="comp-honor-sub">CODEFEST Algothon & UOJ Coders 5.0</div>
        </div>

        <div className="comp-honor-card">
          <div className="comp-honor-top">
            <img
              src="/images/yarlithub.png"
              alt="Yarl IT Hub"
              className="comp-honor-logo"
            />
            <span className="comp-honor-count">7</span>
          </div>
          <div className="comp-honor-label">Finalist Selections</div>
          <div className="comp-honor-sub">Consecutive Finals across 2024–2026</div>
        </div>

        <div className="comp-honor-card">
          <div className="comp-honor-top">
            <img
              src="/images/salesforce.png"
              alt="Yarl Salesforce Ohana / AI"
              className="comp-honor-logo"
            />
            <span className="comp-honor-count">700+</span>
          </div>
          <div className="comp-honor-label">Islandwide Developers</div>
          <div className="comp-honor-sub">Kapruka Agent Challenge 2026</div>
        </div>
      </div>

      {/* ── Tournament Spotlight Selector Tabs (Using Official Logos) ── */}
      <div className="comp-tabs-bar reveal">
        <button
          type="button"
          className={`comp-tab-btn ${activeStage === 'algothon' ? 'active' : ''}`}
          onClick={() => setActiveStage('algothon')}
        >
          <img
            src="/images/codefest2026.png"
            alt="CODEFEST"
            className="comp-tab-logo-img"
          />
          <span>CODEFEST 2026 — Algothon</span>
          <span className="comp-tab-badge">Merit Award</span>
        </button>

        <button
          type="button"
          className={`comp-tab-btn ${activeStage === 'uoj' ? 'active' : ''}`}
          onClick={() => setActiveStage('uoj')}
        >
          <img
            src="/images/uoj_logo.png"
            alt="University of Jaffna"
            className="comp-tab-logo-img"
          />
          <span>UOJ Coders 5.0</span>
          <span className="comp-tab-badge">Top 10 Finalist</span>
        </button>

        <button
          type="button"
          className={`comp-tab-btn ${activeStage === 'kapruka' ? 'active' : ''}`}
          onClick={() => setActiveStage('kapruka')}
        >
          <img
            src="/images/salesforce.png"
            alt="AI Agent"
            className="comp-tab-logo-img"
          />
          <span>Kapruka Agent Challenge</span>
          <span className="comp-tab-badge">AI Shopping Agent</span>
        </button>
      </div>

      {/* ── Active Stage Spotlight ───────────────────────── */}
      <div className="comp-stage-card reveal">
        {/* SPOTLIGHT 1: SLIIT CODEFEST 2026 ALGOTHON */}
        {activeStage === 'algothon' && (
          <div className="comp-stage-inner">
            <div className="comp-stage-col-info">
              <div className="comp-stage-meta">
                <div className="comp-stage-meta-left">
                  <img
                    src="/images/codefest2026.png"
                    alt="SLIIT CODEFEST 2026"
                    className="comp-stage-logo"
                  />
                  <span className="comp-stage-award-badge">
                    <IconStar />
                    <span>Merit Award · Top 10</span>
                  </span>
                </div>
                <div className="comp-stage-meta-right">
                  SLIIT Faculty of Computing · Sep 2026
                </div>
              </div>

              <div className="comp-stage-eyebrow">NATIONAL COMPETITIVE PROGRAMMING HACKATHON</div>
              <h3 className="comp-stage-title">
                CODEFEST 2026<br />
                <em>Algothon Merit Award</em>
              </h3>

              <div className="comp-stage-team-tag">
                <span>Team:</span>
                <strong>CodeMode</strong>
                <span>· Representing SLIIT – Northern UNI</span>
              </div>

              {/* Personal Story Quote Panel */}
              <div className="comp-stage-quote">
                <span className="comp-stage-quote-mark">“</span>
                <p className="comp-stage-quote-text">
                  Proud to receive a Merit Award at CODEFEST 2026! Really happy to receive my Merit Award Certificate for representing <strong>SLIIT – Northern UNI</strong> at Algothon under the Tertiary Category of CODEFEST 2026. This was my first Algothon, and honestly, it was a great experience. From facing challenging problems to trying different approaches under time pressure, every part of the competition pushed me to think differently, stay focused, and keep going when things didn’t go as planned. Getting into the Top 10 and receiving a Merit Award makes this experience even more special.
                </p>
                <span className="comp-stage-quote-author">
                  — Marisilin Bijosilin · First Algothon. First Merit Award. Definitely not the last.
                </span>
              </div>

              <p className="comp-stage-desc">
                Organized by the <strong>Faculty of Computing, Sri Lanka Institute of Information Technology (SLIIT)</strong>. Competed through an intense 8-hour algorithmic challenge involving advanced data structures, algorithmic design, and rigorous performance optimization against university teams islandwide.
              </p>

              {/* Stat Triad */}
              <div className="comp-stat-grid">
                <div className="comp-stat-box gold">
                  <span className="comp-stat-val">Top 10</span>
                  <span className="comp-stat-lbl">Merit Award Winner</span>
                </div>
                <div className="comp-stat-box">
                  <span className="comp-stat-val">8 Hours</span>
                  <span className="comp-stat-lbl">Algothon Crucible</span>
                </div>
                <div className="comp-stat-box">
                  <span className="comp-stat-val">Tertiary</span>
                  <span className="comp-stat-lbl">University Category</span>
                </div>
              </div>

              {/* CTAs */}
              <div className="comp-action-row">
                <button
                  type="button"
                  className="comp-btn-gold"
                  onClick={() => setLightboxImg(algothonGallery[0])}
                >
                  <IconDocument />
                  <span>Inspect Merit Certificate</span>
                  <IconArrowUpRight />
                </button>
                <button
                  type="button"
                  className="comp-btn-ghost"
                  onClick={() => setLightboxImg(currentAlgoImg)}
                >
                  <IconMaximize />
                  <span>View Fullscreen</span>
                </button>
              </div>

              {/* Tech Tags */}
              <div className="comp-stack">
                {['Competitive Programming', 'Algorithms', 'Data Structures', 'Problem Solving', 'Software Engineering', 'CodeMode', 'SLIIT Northern Uni'].map(t => (
                  <span key={t} className="comp-chip">{t}</span>
                ))}
              </div>
            </div>

            {/* Right: Interactive Gallery Viewport */}
            <div className="comp-gallery-col">
              <div
                className="comp-viewport-wrap"
                onClick={() => setLightboxImg(currentAlgoImg)}
                title="Click to view full-resolution image"
              >
                <img
                  src={currentAlgoImg.src}
                  alt={currentAlgoImg.title}
                  className="comp-viewport-img"
                />
                <span className="comp-viewport-zoom-hint">
                  <IconMaximize />
                  <span>Click for Fullscreen</span>
                </span>
                <div className="comp-viewport-caption">{currentAlgoImg.caption}</div>
              </div>

              {/* Thumbnails Navigation */}
              <div className="comp-thumb-grid">
                {algothonGallery.map((item, idx) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`comp-thumb-item ${idx === algoImgIdx ? 'active' : ''}`}
                    onClick={() => setAlgoImgIdx(idx)}
                  >
                    <span>{item.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* SPOTLIGHT 2: UOJ CODERS 5.0 */}
        {activeStage === 'uoj' && (
          <div className="comp-stage-inner">
            <div className="comp-stage-col-info">
              <div className="comp-stage-meta">
                <div className="comp-stage-meta-left">
                  <img
                    src="/images/uoj_logo.png"
                    alt="University of Jaffna"
                    className="comp-stage-logo"
                  />
                  <span className="comp-stage-award-badge blue">
                    <IconStar />
                    <span>Top 10 Finalist</span>
                  </span>
                </div>
                <div className="comp-stage-meta-right">
                  University of Jaffna · Feb 2026
                </div>
              </div>

              <div className="comp-stage-eyebrow">INTER-UNIVERSITY COMPETITIVE CRUCIBLE</div>
              <h3 className="comp-stage-title">
                UOJ Coders v5.0<br />
                <em>Late-Night Crucible</em>
              </h3>

              <div className="comp-stage-team-tag">
                <span>Team:</span>
                <strong>Team_Git_Force_Push</strong>
                <span>· Team Lead representing SLIIT Northern UNI</span>
              </div>

              <p className="comp-stage-desc">
                Led <strong>Team Git_Force_Push</strong> representing <strong>SLIIT Northern UNI</strong> at UoJ Coders v5.0 — organized by the Computer Society & Department of Computer Science, University of Jaffna.
              </p>
              <p className="comp-stage-desc" style={{ marginTop: '-0.75rem' }}>
                An intense <strong>12-hour late-night competitive programming battle</strong> alongside 40 top university teams across Sri Lanka. A relentless test of algorithm optimization, dynamic programming, rapid debugging, and teamwork under pressure, placing firmly in the <strong>Top 10</strong>.
              </p>

              {/* Stat Triad */}
              <div className="comp-stat-grid">
                <div className="comp-stat-box gold">
                  <span className="comp-stat-val">Top 10</span>
                  <span className="comp-stat-lbl">Islandwide Finalist</span>
                </div>
                <div className="comp-stat-box">
                  <span className="comp-stat-val">12 Hours</span>
                  <span className="comp-stat-lbl">Overnight Crucible</span>
                </div>
                <div className="comp-stat-box">
                  <span className="comp-stat-val">Team Lead</span>
                  <span className="comp-stat-lbl">Git_Force_Push</span>
                </div>
              </div>

              {/* CTAs */}
              <div className="comp-action-row">
                <button
                  type="button"
                  className="comp-btn-primary"
                  onClick={() => setLightboxImg(uojGallery[0])}
                >
                  <IconDocument />
                  <span>Inspect UoJ Certificate</span>
                  <IconArrowUpRight />
                </button>
                <a
                  href="https://www.linkedin.com/posts/marisilin-bijosilin_uojcodersv5-uojcoders-teamgitforcepush-share-7484632746812780544-9LhU"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="comp-btn-ghost"
                >
                  <span>LinkedIn Post</span>
                  <IconArrowUpRight />
                </a>
              </div>

              {/* Tech Tags */}
              <div className="comp-stack">
                {['Competitive Programming', 'Algorithms', 'Data Structures', 'Team Lead', 'SLIIT Northern Uni', 'University of Jaffna'].map(t => (
                  <span key={t} className="comp-chip">{t}</span>
                ))}
              </div>
            </div>

            {/* Right: Interactive Gallery Viewport */}
            <div className="comp-gallery-col">
              <div
                className="comp-viewport-wrap"
                onClick={() => setLightboxImg(currentUojImg)}
                title="Click to view full-resolution image"
              >
                <img
                  src={currentUojImg.src}
                  alt={currentUojImg.title}
                  className="comp-viewport-img"
                />
                <span className="comp-viewport-zoom-hint">
                  <IconMaximize />
                  <span>Click for Fullscreen</span>
                </span>
                <div className="comp-viewport-caption">{currentUojImg.caption}</div>
              </div>

              {/* Thumbnails Navigation */}
              <div className="comp-thumb-grid">
                {uojGallery.map((item, idx) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`comp-thumb-item ${idx === uojImgIdx ? 'active' : ''}`}
                    onClick={() => setUojImgIdx(idx)}
                  >
                    <span>{item.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* SPOTLIGHT 3: KAPRUKA AGENT CHALLENGE 2026 */}
        {activeStage === 'kapruka' && (
          <div className="comp-stage-inner">
            <div className="comp-stage-col-info">
              <div className="comp-stage-meta">
                <div className="comp-stage-meta-left">
                  <span className="comp-stage-award-badge">
                    <IconStar />
                    <span>700+ Developers · Certified</span>
                  </span>
                </div>
                <div className="comp-stage-meta-right">
                  Kapruka Holdings PLC (KPHL) · Feb 2026
                </div>
              </div>

              <div className="comp-stage-eyebrow">AGENTIC AI & MODEL CONTEXT PROTOCOL</div>
              <h3 className="comp-stage-title">
                Kapruka AI<br />
                <em>Shopping Agent</em>
              </h3>

              <div className="comp-stage-team-tag">
                <span>Format:</span>
                <strong>National AI Challenge</strong>
                <span>· 700+ Islandwide Engineers</span>
              </div>

              <p className="comp-stage-desc">
                Proud recipient of the <strong>Certificate of Participation</strong> from <strong>Kapruka Holdings PLC (KPHL)</strong> for competing in the <strong>Kapruka Agent Challenge 2026</strong> under Founder & Chairman Dulith Herath.
              </p>
              <p className="comp-stage-desc" style={{ marginTop: '-0.75rem' }}>
                Engineered an autonomous AI agent leveraging the <strong>Kapruka Model Context Protocol (MCP)</strong> for conversational gift discovery, personalized product recommendations, and end-to-end e-commerce checkout integration deployed to Cloudflare Pages.
              </p>

              {/* Stat Triad */}
              <div className="comp-stat-grid">
                <div className="comp-stat-box gold">
                  <span className="comp-stat-val">MCP</span>
                  <span className="comp-stat-lbl">Model Context Protocol</span>
                </div>
                <div className="comp-stat-box">
                  <span className="comp-stat-val">700+</span>
                  <span className="comp-stat-lbl">Islandwide Devs</span>
                </div>
                <div className="comp-stat-box">
                  <span className="comp-stat-val">Verified</span>
                  <span className="comp-stat-lbl">Kapruka Holdings PLC</span>
                </div>
              </div>

              {/* CTAs */}
              <div className="comp-action-row">
                <a
                  href="https://ai-shopping-agent.pages.dev/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="comp-btn-primary"
                >
                  <span>View Live Agent</span>
                  <IconArrowUpRight />
                </a>
                <button
                  type="button"
                  className="comp-btn-ghost"
                  onClick={() => setLightboxImg({
                    title: 'Kapruka Agent Challenge Certificate',
                    src: '/images/Kapruka_Agent_Challenge_Certificate.jpg',
                    caption: 'Official Certificate of Participation — Kapruka Agent Challenge 2026, Kapruka Holdings PLC'
                  })}
                >
                  <IconDocument />
                  <span>View Certificate</span>
                </button>
                <a
                  href="https://github.com/Bijon2002/AI-shopping-agent"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="comp-btn-ghost"
                >
                  <span>GitHub Repo</span>
                  <IconArrowUpRight />
                </a>
              </div>

              {/* Tech Tags */}
              <div className="comp-stack">
                {['Agentic AI', 'MCP Protocol', 'Claude AI', 'React.js', 'TypeScript', 'Cloudflare Pages'].map(t => (
                  <span key={t} className="comp-chip">{t}</span>
                ))}
              </div>
            </div>

            {/* Right: Interactive Preview / Certificate Toggle */}
            <div className="comp-gallery-col">
              <div className="comp-thumb-grid" style={{ marginBottom: '0.75rem' }}>
                <button
                  type="button"
                  className={`comp-thumb-item ${kaprukaTab === 'live' ? 'active' : ''}`}
                  onClick={() => setKaprukaTab('live')}
                >
                  <IconGlobe />
                  <span>Live Agent Preview</span>
                </button>
                <button
                  type="button"
                  className={`comp-thumb-item ${kaprukaTab === 'cert' ? 'active' : ''}`}
                  onClick={() => setKaprukaTab('cert')}
                >
                  <IconDocument />
                  <span>Official Certificate</span>
                </button>
              </div>

              {kaprukaTab === 'live' ? (
                <div className="comp-iframe-shell">
                  <iframe
                    src="https://ai-shopping-agent.pages.dev/"
                    title="Kapruka AI Shopping Agent"
                    className="comp-iframe"
                    loading="lazy"
                  />
                  <div
                    className="comp-iframe-overlay"
                    onClick={() => window.open('https://ai-shopping-agent.pages.dev/', '_blank')}
                  >
                    <span className="comp-overlay-hint">
                      <span>Click to Open Fullscreen</span>
                      <IconArrowUpRight />
                    </span>
                  </div>
                </div>
              ) : (
                <div
                  className="comp-viewport-wrap"
                  onClick={() => setLightboxImg({
                    title: 'Kapruka Agent Challenge Certificate',
                    src: '/images/Kapruka_Agent_Challenge_Certificate.jpg',
                    caption: 'Official Certificate of Participation — Kapruka Agent Challenge 2026, Kapruka Holdings PLC'
                  })}
                >
                  <img
                    src="/images/Kapruka_Agent_Challenge_Certificate.jpg"
                    alt="Kapruka Certificate"
                    className="comp-viewport-img"
                  />
                  <span className="comp-viewport-zoom-hint">
                    <IconMaximize />
                    <span>Click for Fullscreen</span>
                  </span>
                  <div className="comp-viewport-caption">Kapruka Agent Challenge 2026 — Official Certificate of Participation</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── All Competitions Chronicle (Hall of Fame) ───── */}
      <div className="comp-chronicle-shell reveal">
        <div className="comp-chronicle-top">
          <div className="comp-chronicle-heading-group">
            <span className="comp-chronicle-kicker">HALL OF FAME & CHRONOLOGY</span>
            <h3 className="comp-chronicle-h3">All Competitions & Hackathons</h3>
          </div>

          {/* Year Filter Tabs */}
          <div className="comp-filter-tabs">
            {['All', '2026', '2025', '2024'].map(year => (
              <button
                key={year}
                type="button"
                className={`comp-filter-tab ${selectedYear === year ? 'active' : ''}`}
                onClick={() => setSelectedYear(year)}
              >
                {year === 'All' ? 'All Seasons (7)' : `${year} Season (${competitions.filter(c => c.year === year).length})`}
              </button>
            ))}
          </div>
        </div>

        {/* Competition Cards Grid */}
        <div className="comp-cards-grid">
          {filteredCompetitions.map(comp => {
            const isGold = comp.achievement.includes('Merit Award') || comp.achievement.includes('Top 10');
            return (
              <div
                key={comp._id}
                className={`comp-grid-item ${comp.isFeatured ? 'featured' : ''}`}
              >
                <div>
                  <div className="comp-item-header">
                    <div className="comp-item-org">
                      {comp.organizerLogo ? (
                        <img
                          src={comp.organizerLogo}
                          alt={comp.organizer}
                          className="comp-item-org-img"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : null}
                      <div className="comp-item-org-text">
                        <span className="comp-item-org-name">{comp.organizer}</span>
                        <span className="comp-item-date">{comp.date}</span>
                      </div>
                    </div>
                    <span className="comp-item-type">{comp.type}</span>
                  </div>

                  <div className={`comp-item-ribbon ${isGold ? 'gold' : ''}`}>
                    <IconStar />
                    <span>{comp.achievement}</span>
                  </div>

                  <h4 className="comp-item-title">{comp.title}</h4>
                  <div className="comp-item-subtitle">{comp.subtitle}</div>

                  <span className="comp-item-team">
                    Team: <strong>{comp.teamName}</strong>
                  </span>

                  <p className="comp-item-desc">{comp.description}</p>

                  {/* Certificate preview trigger if available */}
                  {comp.certificate && (
                    <button
                      type="button"
                      className="comp-item-cert-btn"
                      onClick={() => setLightboxImg({
                        title: `${comp.title} Certificate`,
                        src: comp.certificate,
                        caption: `Official Record / Certificate for ${comp.title} (${comp.organizer})`
                      })}
                    >
                      <IconDocument />
                      <span>View Official Certificate</span>
                      <IconArrowUpRight />
                    </button>
                  )}
                </div>

                <div className="comp-item-tags">
                  {comp.tags.map(tag => (
                    <span key={tag} className="comp-item-tag">{tag}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── High-Resolution Lightbox Modal ─────────────── */}
      <LightboxModal
        image={lightboxImg}
        onClose={() => setLightboxImg(null)}
      />
    </section>
  );
};

export default Competitions;
